import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { JsonValueInput } from '~/api/rolodex/typedefs.autogen';
import type { PinsByScope } from '~/hooks/usePinnedFields';
import type { RootState } from '~/rootState';

import AuthenticationStore from '~/auth/authenticationStore';
import { useDebounce } from '~/eds/src/hooks/useDebounce';
import { LocalStorageKeyType } from '~/enums/LocalStorageKeyType';
import { useAppDispatch } from '~/rootState';

import type { UserPreferenceKey } from './enhancements';
import type {
  DetailsPanelPreferences,
  UserPreferenceDefinition,
} from './useUserPreferences.types';

import { rolodex } from './index';

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((entry) => typeof entry === 'string')
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parsePinsByScope(value: unknown): PinsByScope {
  if (!isRecord(value)) return {};
  return Object.entries(value).reduce<PinsByScope>((pins, [scope, ids]) => {
    if (isStringArray(ids)) pins[scope] = ids;
    return pins;
  }, {});
}

function parseWidthByArea(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  return Object.entries(value).reduce<Record<string, number>>(
    (widths, [area, width]) => {
      if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
        widths[area] = width;
      }
      return widths;
    },
    {},
  );
}

const PINNED_NAV_ITEMS: UserPreferenceDefinition<string[]> = {
  defaultValue: [],
  parse: (value) => (isStringArray(value) ? value : []),
  debounceMs: 500,
};

const SIDEBAR_NAV_OPT_IN: UserPreferenceDefinition<boolean> = {
  defaultValue: false,
  parse: (value) => typeof value === 'boolean' && value,
  localStorageKey: LocalStorageKeyType.SidebarNavOptIn,
};

const SIDEBAR_NAV_COLLAPSED: UserPreferenceDefinition<boolean> = {
  defaultValue: false,
  parse: (value) => typeof value === 'boolean' && value,
  localStorageKey: LocalStorageKeyType.SidebarNavCollapsed,
};

const EMPTY_DETAILS_PANEL_PREFERENCES: DetailsPanelPreferences = {
  widthByArea: {},
  pinnedFieldsByDocumentType: {},
  pinnedFieldsByEntityType: {},
  pinnedFieldsByWorkflow: {},
};

const DETAILS_PANEL_PREFERENCES: UserPreferenceDefinition<DetailsPanelPreferences> =
  {
    defaultValue: EMPTY_DETAILS_PANEL_PREFERENCES,
    parse: (value) => {
      if (!isRecord(value)) return EMPTY_DETAILS_PANEL_PREFERENCES;
      return {
        widthByArea: parseWidthByArea(value.widthByArea),
        pinnedFieldsByDocumentType: parsePinsByScope(
          value.pinnedFieldsByDocumentType,
        ),
        pinnedFieldsByEntityType: parsePinsByScope(
          value.pinnedFieldsByEntityType,
        ),
        pinnedFieldsByWorkflow: parsePinsByScope(value.pinnedFieldsByWorkflow),
      };
    },
    debounceMs: 500,
    localStorageKey: LocalStorageKeyType.DetailsPanelPreferences,
  };

function readLocalCache<V>(
  key: LocalStorageKeyType | undefined,
  fallback: V,
): V {
  if (!key) return fallback;
  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : fallback;
  } catch {
    return fallback;
  }
}

function writeLocalCache<V>(key: LocalStorageKeyType | undefined, value: V) {
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function usePreference<V extends JsonValueInput>(
  key: UserPreferenceKey,
  definition: UserPreferenceDefinition<V>,
) {
  const dispatch = useAppDispatch();
  // Reads the imperative store rather than `useAuthenticationStore()`: this
  // provider mounts unconditionally in `renderWithDependencies`, ahead of
  // whatever the test actually renders, so a `mockReturnValueOnce` aimed at a
  // component further down the tree would be consumed by this call first
  // instead. The imperative read is a render-time snapshot rather than a
  // subscription, but every route that cares about a login transition remounts
  // this whole provider tree anyway (see `routes.tsx`), so staleness within a
  // single mount isn't a real risk here.
  const authStore = AuthenticationStore();
  // Optional chaining throughout, not a plain call and destructure: several
  // unrelated tests replace this whole module with a hand-written mock that
  // only defines the one or two rolodex hooks (or auth store methods) they
  // actually exercise — the rest are absent there entirely, not just stubbed.
  // Whichever it is stays fixed for the lifetime of a given render tree, so
  // calling it (or not) through optional chaining doesn't make this a
  // conditional hook call.
  const preferenceQuery = rolodex.useGetUserPreferenceQuery?.(key, {
    skip: !authStore.isLoggedIn?.() || Boolean(authStore.requiresReauth?.()),
  });
  const raw = preferenceQuery?.data;
  const isLoading = preferenceQuery?.isLoading ?? false;
  const setUserPreference = rolodex.useSetUserPreferenceMutation?.()?.[0];

  const parseCached = useCallback(
    (cached: unknown): V =>
      cached === undefined
        ? readLocalCache(definition.localStorageKey, definition.defaultValue)
        : definition.parse(cached),
    [definition],
  );

  // Memoized because `parse` builds a fresh value for the object-shaped
  // preferences, and consumers key their own memos off this identity.
  const value = useMemo(() => parseCached(raw), [parseCached, raw]);

  // What the last write resolved to, for the flush below when there is no
  // cache entry to read (this module auto-mocked, or `upsertQueryData` absent).
  const writtenValueRef = useRef<V | undefined>(undefined);

  const selectCached = useCallback(
    (state: RootState) =>
      rolodex.endpoints?.getUserPreference?.select?.(key)?.(state)?.data,
    [key],
  );

  // Mirror what the service returned so the next first render reads it
  // synchronously. A consumer that seeds a child on mount (the details panel
  // seeds `ResizableLayout`'s width) can't adopt a value that only resolves
  // later in that same mount, so without this the stored value would never
  // apply on a device that has not written it locally yet.
  useEffect(() => {
    if (raw !== undefined) {
      writeLocalCache(definition.localStorageKey, value);
    }
  }, [raw, value, definition]);

  // Sends whatever the cache holds when the timer fires, not a body captured
  // when the write was requested. A whole-record PUT built earlier can be
  // missing a field another hook changed in the meantime, and would put the
  // service back to that older shape.
  const saveValue = useDebounce(
    () =>
      dispatch((_innerDispatch, getState) => {
        const cached = selectCached(getState());
        const pending =
          cached === undefined ? writtenValueRef.current : parseCached(cached);
        if (pending === undefined) return;
        setUserPreference?.({ key, value: pending });
      }),
    definition.debounceMs ?? 0,
    [setUserPreference, dispatch, key, selectCached, parseCached],
  );

  // A save in flight when the tab goes away would otherwise be cancelled with
  // the debounce timer, losing the last update someone made.
  useEffect(() => {
    const flush = () => saveValue.flush();
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [saveValue]);

  /**
   * Accepts an updater as well as a value, resolved inside the dispatch
   * against what the cache holds at that moment rather than what the calling
   * render read. Several hooks write one record (a panel's width from the page
   * shell, its pinned fields from the panel), so two writes before a re-render
   * would otherwise both build on the same stale copy and the second would
   * drop the first's fields.
   */
  const setValue = useCallback(
    (next: V | ((previous: V) => V)) => {
      dispatch((innerDispatch, getState) => {
        const previous = parseCached(selectCached(getState()));
        const resolved = next instanceof Function ? next(previous) : next;

        // `upsertQueryData` rather than `updateQueryData`: a preference nobody
        // has set reads back as a 204 with no body, so there is no cache entry
        // for a patch to land on and the write would not show until a reload.
        // Upserting creates the entry, so every instance reads one value.
        // See the comment above: `rolodex.util` is a stub with nothing
        // `dispatch`-able when this module is auto-mocked.
        const upsert = rolodex.util?.upsertQueryData?.(
          'getUserPreference',
          key,
          resolved,
        );
        if (upsert) innerDispatch(upsert);
        writtenValueRef.current = resolved;
        writeLocalCache(definition.localStorageKey, resolved);
        saveValue();
      });
    },
    [dispatch, key, definition, saveValue, parseCached, selectCached],
  );

  return [value, setValue, isLoading] as const;
}

/**
 * Every rolodex-backed user preference the app reads and writes, each keyed
 * under the same generic preferences store (`~/redux/api/rolodex/enhancements`).
 * Add a new preference by giving it a definition here, not new endpoints.
 */
export function useUserPreferences() {
  const [pinnedNavItems, setPinnedNavItems] = usePreference(
    'pinnedNavItems',
    PINNED_NAV_ITEMS,
  );
  const [sidebarNavOptIn, setSidebarNavOptIn, isSidebarNavOptInLoading] =
    usePreference('sidebarNavOptIn', SIDEBAR_NAV_OPT_IN);
  const [sidebarNavCollapsed, setSidebarNavCollapsed] = usePreference(
    'sidebarNavCollapsed',
    SIDEBAR_NAV_COLLAPSED,
  );
  const [detailsPanelPreferences, setDetailsPanelPreferences] = usePreference(
    'detailsPanelPreferences',
    DETAILS_PANEL_PREFERENCES,
  );

  return {
    pinnedNavItems,
    setPinnedNavItems,
    sidebarNavOptIn,
    setSidebarNavOptIn,
    isSidebarNavOptInLoading,
    sidebarNavCollapsed,
    setSidebarNavCollapsed,
    detailsPanelPreferences,
    setDetailsPanelPreferences,
  };
}
